<template>
  <div v-loading="scene_state.loading"
       class="sku-validation-scene"
       :element-loading-text="$t('Image is loading')">
    <div v-if="scene_state.error" class="error">
      {{ $t('An error has occured while receiving information') }}
    </div>
    <el-tooltip ref="toolTipElement"
                v-model="show_tooltip"
                :popper-options="popperOptions"
                placement="bottom"
                :manual="true"
                popper-class="sku-validation-scene__tooltip"
                effect="light">
      <span :style="tooltip.style" />
      <div slot="content"
           class="sku-validation-scene__tooltip-content"
           :class="{ 'is-unrecognized': isUnrecognized, 'is-meta': isMeta}">
        <SkuPreview v-if="crop"
                    :key="crop.crop.crop_id"
                    :project-id="projectId"
                    :sku-id="crop.crop.class.id"
                    :non-sku="isNonSku"
                    :is-competitor="isCompetitor"
                    :is-meta="isMeta"
                    :is-unrecognized="isUnrecognized"
                    :is-sku="isSku"
                    :is-unlink-button-disabled="crop && selectedCropWasProcessed && crop.crop.crop_id !== selectedCropWasProcessed.crop_id"
                    :non-sku-list="nonSkuList"
                    :crop-data="cropData" />
        <template v-if="selectedCropWasProcessed">
          <i class="mdi mdi-arrow-right" />
          <div class="selected-preview-box">
            <SkuPreview :key="selectedCropWasProcessed.id"
                        :project-id="projectId"
                        :sku-id="selectedCropWasProcessed.id"
                        :non-sku="isNonSku"
                        :is-competitor="isCompetitor"
                        :non-sku-list="nonSkuList" />
            <el-button v-if="canUndo"
                       :loading="bus.validation_request"
                       @click="bus.cancelSkuValidation">
              {{ $t('Undo') }}
            </el-button>
          </div>
        </template>
        <el-button v-if="isUnrecognized || isMeta"
                   type="primary"
                   :loading="componentBus.isSkuCandidateCreating"
                   @click="emitAddToPool">
          {{ $t('+ ADD TO POOL') }}
        </el-button>
      </div>
    </el-tooltip>
  </div>
</template>

<script>
import { result } from 'lodash';
import SkuValidationScene from '../sku-validation-scene';
import bus, { EVENTS } from '../sku-validation-scene/bus';
import SkuPreview from './SkuPreview';
import componentBus, { EVENTS as componentBusEvents } from './componentBus';

export default {
  name: 'SkuValidationScene',
  components: { SkuPreview },
  props: {
    projectId: {
      type: [String, Number],
      required: true
    },
    scene: {
      type: Object,
      required: true
    },
    canUndo: {
      type: Boolean,
      default: false
    },
    nonSkuList: {
      type: Array,
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      bus,
      componentBus,
      scene_state: {
        loading: false,
        error: false
      },
      crop: null,
      show_tooltip: false,
      popperOptions: {},
      cropData: new Map()
    };
  },
  computed: {
    isNonSku() {
      return result(this.crop, 'crop.is_nonSku') === true;
    },
    isMeta() {
      return result(this.crop, 'crop.is_meta');
    },
    isSku() {
      return result(this.crop, 'crop.is_sku');
    },
    isCompetitor() {
      return result(this.crop, 'crop.is_competitor');
    },
    isUnrecognized() {
      return result(this.crop, 'crop.is_unrecognized');
    },
    selectedCropWasProcessed() {
      const { ValidatedCrops } = this.bus;

      if (!this.crop) return false;

      return ValidatedCrops.data.body.find(crop => crop.crop_id === this.crop.crop.crop_id);
    },
    tooltip() {
      if (!this.crop) return {};

      const {
        x, y, width, height
      } = this.crop.bounds;

      return {
        style: {
          position: 'absolute',
          cursor: 'pointer',
          zIndex: -2,
          width: `${width}px`,
          height: `${height}px`,
          left: `${x}px`,
          top: `${y}px`
        }
      };
    }
  },
  watch: {
    scene: {
      deep: true,
      handler() {
        this.destroyScene();
        this.setupScene();
      }
    }
  },
  async mounted() {
    this.setupScene();
    this.popperOptions = { boundariesElement: this.$el };
    this.$refs.toolTipElement.updatePopper();

    bus.$on(EVENTS.TOGGLE_EDIT_MODE, this.handleEditMode);
  },
  beforeDestroy() {
    bus.$off(EVENTS.TOGGLE_EDIT_MODE, this.handleEditMode);
    this.destroyScene();
  },
  methods: {
    emitAddToPool() {
      componentBus.$emit(componentBusEvents.ADD_TO_POOL);
    },
    handleEditMode() {
      this.$nextTick(() => {
        const { width, height } = this.$el.getBoundingClientRect();
        this.scene_canvas.renderer.resize(width, height);
      });
    },
    showToolTip(crop) {
      this.crop = crop;
      this.$nextTick().then(() => {
        this.show_tooltip = true;
        this.$refs.toolTipElement.updatePopper();
        this.$refs.toolTipElement.show();
      });
    },
    destroyToolTip() {
      this.$nextTick().then(() => {
        this.show_tooltip = false;
        this.$refs.toolTipElement.hide();
      });
    },
    updateToolTip() {
      this.$nextTick().then(() => {
        this.$refs.toolTipElement.updatePopper();
      });
    },
    async setupScene() {
      this.scene_state.loading = true;
      this.scene_state.error = false;

      try {
        bus.$on(EVENTS.SHOW_TOOL_TIP, c => this.showToolTip(c));
        bus.$on(EVENTS.DESTROY_TOOLTIP, c => this.destroyToolTip(c));
        bus.$on(EVENTS.RE_RENDER_CROP, () => this.updateToolTip());

        const { width, height } = this.$el.getBoundingClientRect();
        this.scene_canvas = new SkuValidationScene({ width, height: height - 10 }, this.scene);
        await this.scene_canvas.setupMaterials();
        this.$el.append(this.scene_canvas.view);
      } catch (error) {
        this.scene_state.error = true;
        console.warn(error);
      }

      this.scene_state.loading = false;
    },
    destroyScene() {
      const { scene_canvas } = this;

      if (scene_canvas) {
        scene_canvas.removeCustomEvents();
        scene_canvas.renderer.gl.getExtension('WEBGL_lose_context').loseContext();
        scene_canvas.destroy(true);
        scene_canvas.viewport.destroy(true);

        this.scene_canvas = null;
      }

      bus.viewport = null;
      bus.selected_crop = null;
      bus.$off();

      this.show_tooltip = false;
      this.$refs.toolTipElement.hide();
      PIXI.utils.destroyTextureCache();
      PIXI.utils.clearTextureCache();
    }
  }
};
</script>

<style lang="scss">
.sku-validation-scene {
  position: relative;
  flex: 1;
  height: 100%;
  overflow: hidden;

  &__tooltip {
    z-index: 10 !important;
    border: 0 !important;
    box-shadow: 0 0 8px 0 rgba($black, 0.1);

    &-content {
      display: flex;
      align-items: center;

      &.is-unrecognized {
        flex-direction: column;

        .el-button {
          width: 100%;
        }
      }

      &.is-meta {
        flex-direction: column;

        .el-button {
          width: 100%;
        }
      }

      > i {
        font-size: 40px;
        opacity: 0.3;
      }

      .selected-preview-box {
        .el-button {
          width: 100%;
        }
      }
    }
  }
}
</style>
